/* Stage 5 — Combined B9/B10: Workstage 6 (Paid Services) admin UI.

   Full-width vertical stack per §3 of the brief.  Renders authoritative
   records from /api/admin/projects/{pid}/ws6 aggregate GET; no
   package-local duplicate stores.  561-01/02/04 rendered; 561-03/05/06
   surfaced through contextual routing and testid-addressable panels.
*/
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import RecordExternalExecutionModal, {
    EXT_METHOD_LABELS as _METHOD_LABELS,
    uuid4 as _uuid4,
} from "./RecordExternalExecutionModal";
import { usePageMeta } from "../../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Pill({ tone = "neutral", children, testid }) {
    const map = {
        neutral: "bg-[#F5F3EE] text-[#1D1D1B]",
        good: "bg-[#E4EFE4] text-[#345E34]",
        warn: "bg-[#FBEEDF] text-[#7A4A22]",
        active: "bg-[#A8543F] text-white",
    };
    return (
        <span data-testid={testid}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${map[tone]}`}>
            {children}
        </span>);
}

function ModuleCard({ n, title, testid, children }) {
    return (
        <section data-testid={testid}
                    className="border border-[#E5E2DC] rounded-lg bg-white p-4 shadow-sm">
            <header className="mb-3 flex items-baseline gap-3">
                <span className="font-manrope text-[11px] uppercase tracking-widest text-[#A8543F]">
                    Module {n}
                </span>
                <h2 className="font-manrope text-[15px] font-semibold text-[#1D1D1B]">
                    {title}
                </h2>
            </header>
            <div className="text-[13px] text-[#1D1D1B]">{children}</div>
        </section>);
}

export function WS6ActiveModules({ projectId, mode = "active" }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/admin/projects/${projectId}/ws6`,
                                              { withCredentials: true });
            setData(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (err) return <div data-testid="ws6-error" className="text-[13px] text-red-700">{err}</div>;
    if (!data) return <div data-testid="ws6-loading" className="text-[13px] text-[#666]">Loading WS6…</div>;

    const packages = data.packages || [];
    const contracts = data.contract_records || [];
    const changes = data.contract_changes || [];
    const cs = data.commercial_summary || {};
    const shared = data.shared_records || {};

    // D5 Slice D — coherent mode propagation via fieldset gate.
    const readOnly = mode !== "active";
    return (
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws6-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws6-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-4" data-testid="ws6-active-modules">

            {/* Module 1 — Paid Services Overview */}
            <ModuleCard n={1} title="Paid Services Overview"
                            testid="ws6-module-1-overview">
                <div className="grid grid-cols-3 gap-4 text-[12px]">
                    <div data-testid="ws6-overview-packages-count">
                        <div className="text-[#666] uppercase text-[10px]">Packages</div>
                        <div className="text-[15px] font-semibold">{packages.length}</div>
                    </div>
                    <div data-testid="ws6-overview-contracts-count">
                        <div className="text-[#666] uppercase text-[10px]">Contract Records</div>
                        <div className="text-[15px] font-semibold">{contracts.length}</div>
                    </div>
                    <div data-testid="ws6-overview-changes-count">
                        <div className="text-[#666] uppercase text-[10px]">Contract Changes</div>
                        <div className="text-[15px] font-semibold">{changes.length}</div>
                    </div>
                </div>
            </ModuleCard>

            {/* Module 2 — Service Packages */}
            <ModuleCard n={2} title="Service Packages"
                            testid="ws6-module-2-packages">
                {packages.length === 0
                    ? <div data-testid="ws6-no-packages" className="text-[#666]">No packages yet.</div>
                    : (
                        <table className="w-full text-[12px]" data-testid="ws6-packages-table">
                            <thead className="text-left text-[10px] uppercase text-[#666]">
                                <tr>
                                    <th className="py-1">Package</th>
                                    <th>Code</th>
                                    <th>Status</th>
                                    <th>Contract Basis</th>
                                    <th>Fee</th>
                                    <th>Ready</th>
                                </tr>
                            </thead>
                            <tbody>
                                {packages.map(p => (
                                    <tr key={p.id} className="border-t border-[#F0EDE7]"
                                        data-testid={`ws6-pkg-row-${p.id}`}>
                                        <td className="py-1.5">{p.package_name}</td>
                                        <td className="text-[#666]">
                                            <span data-testid={`ws6-pkg-code-${p.id}`}>{p.package_code}</span>
                                        </td>
                                        <td>
                                            <Pill tone={p.status === "Active" ? "good"
                                                        : p.status === "Appointed" ? "active" : "neutral"}
                                                     testid={`ws6-pkg-status-${p.id}`}>
                                                {p.status}
                                            </Pill>
                                        </td>
                                        <td className="text-[#666]" data-testid={`ws6-pkg-basis-${p.id}`}>
                                            {p.contract_basis || (p.snapshot?.contract_basis) || "—"}
                                        </td>
                                        <td className="text-[#666]" data-testid={`ws6-pkg-fee-${p.id}`}>
                                            {p.snapshot?.fixed_fee
                                                ? `${p.snapshot.fixed_fee.currency || "GBP"} ${p.snapshot.fixed_fee.amount || 0}`
                                                : "—"}
                                        </td>
                                        <td>
                                            <Pill tone={p.activation_readiness?.ready ? "good" : "warn"}
                                                     testid={`ws6-pkg-ready-${p.id}`}>
                                                {p.activation_readiness?.ready ? "ready" : "blocked"}
                                            </Pill>
                                        </td>
                                        <td className="text-right">
                                            <Link to={`/admin/projects/${projectId}/packages/${p.id}`}
                                                    data-testid={`ws6-pkg-open-${p.id}`}
                                                    className="text-[#A8543F] underline">
                                                Open package
                                            </Link>
                                            {p.status === "Appointed" ? (
                                                <>
                                                    {" · "}
                                                    <Link to={`/admin/projects/${projectId}/packages/${p.id}/activation`}
                                                            data-testid={`ws6-pkg-review-activation-${p.id}`}
                                                            className="text-[#A8543F] underline">
                                                        Review Activation
                                                    </Link>
                                                </>
                                            ) : null}
                                        </td>
                                    </tr>))}
                            </tbody>
                        </table>)}
            </ModuleCard>

            {/* Module 3 — Programme (summary; A5 authoritative) */}
            <ModuleCard n={3} title="Programme"
                            testid="ws6-module-3-programme">
                <div className="text-[12px] text-[#666]"
                        data-testid="ws6-programme-summary">
                    {shared.programme_items_count || 0} programme item(s) linked via A5.
                    Global Programme editor is B11.
                </div>
            </ModuleCard>

            {/* Module 4 — Internal Team To-Dos (summary; A5/B3 authoritative) */}
            <ModuleCard n={4} title="Internal Team To-Dos"
                            testid="ws6-module-4-internal-todos">
                <div className="text-[12px] text-[#666]"
                        data-testid="ws6-internal-todos-summary">
                    {shared.internal_tasks_open || 0} open · {shared.internal_tasks_count || 0} total
                    · authoritative source A5/B3.
                </div>
            </ModuleCard>

            {/* Module 5 — Client Actions (summary; A6/B3 authoritative) */}
            <ModuleCard n={5} title="Client Actions"
                            testid="ws6-module-5-client-actions">
                <div className="text-[12px] text-[#666]"
                        data-testid="ws6-client-actions-summary">
                    {shared.client_actions_open || 0} open · {shared.client_actions_count || 0} total
                    · authoritative source A6/B3.
                </div>
            </ModuleCard>

            {/* Module 6 — Deliverables / Issues (summary; A4/B7a authoritative) */}
            <ModuleCard n={6} title="Deliverables / Design Issues"
                            testid="ws6-module-6-deliverables">
                <div className="text-[12px] text-[#666]"
                        data-testid="ws6-deliverables-summary">
                    {shared.deliverables_count || 0} deliverable(s) · {shared.design_issues_count || 0} design issue(s)
                    · authoritative source A4/B7a.
                </div>
            </ModuleCard>

            {/* Module 7 — Commercial / Contractual */}
            <ModuleCard n={7} title="Commercial / Contractual"
                            testid="ws6-module-7-commercial">
                <div className="mb-2">
                    <Link to={`/admin/projects/${projectId}/commercial-hub`}
                            data-testid="ws6-open-commercial-hub"
                            className="text-[#A8543F] underline text-[12px]">
                        Open Commercial / Contractual Hub →
                    </Link>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12px]">
                    <div data-testid="ws6-cs-lrs-fees">
                        <span className="text-[#666]">Service fees:</span>{" "}
                        <strong>GBP {cs.lrs_service_fees || 0}</strong>
                    </div>
                    <div data-testid="ws6-cs-construction-sum">
                        <span className="text-[#666]">Construction Contract Sum:</span>{" "}
                        <strong>GBP {cs.construction_contract_sum || 0}</strong>
                    </div>
                    <div data-testid="ws6-cs-adjusted-sum">
                        <span className="text-[#666]">Adjusted Contract Sum:</span>{" "}
                        <strong>GBP {cs.adjusted_construction_contract_sum || 0}</strong>
                    </div>
                    <div data-testid="ws6-cs-paid">
                        <span className="text-[#666]">Paid to date:</span>{" "}
                        <strong>{cs.paid_to_date}</strong>
                    </div>
                </div>
                <div className="mt-2 text-[11px] text-[#666]"
                        data-testid="ws6-cs-notes">
                    {cs.notes}
                </div>
                <div className="mt-3">
                    <h4 className="font-manrope text-[11px] uppercase text-[#666] tracking-wide">
                        Contract Records
                    </h4>
                    {contracts.length === 0
                        ? <div data-testid="ws6-no-contracts" className="text-[#666] text-[12px]">None yet.</div>
                        : (
                            <ul data-testid="ws6-contracts-list" className="text-[12px]">
                                {contracts.map(c => (
                                    <li key={c.id} className="py-0.5"
                                        data-testid={`ws6-contract-row-${c.id}`}>
                                        <span data-testid={`ws6-contract-title-${c.id}`}>
                                            {c.contract_title}
                                        </span>{" — "}
                                        <span className="text-[#666]"
                                                data-testid={`ws6-contract-basis-${c.id}`}>
                                            {c.contract_basis}
                                        </span>{" — "}
                                        <Pill tone={c.execution_state === "executed" ? "good" : "warn"}
                                                 testid={`ws6-contract-exec-${c.id}`}>
                                            {c.execution_state}
                                        </Pill>
                                        <Pill tone={c.effective_state === "effective" ? "good" : "warn"}
                                                 testid={`ws6-contract-eff-${c.id}`}>
                                            {c.effective_state}
                                        </Pill>
                                        {" · "}
                                        <Link to={`/admin/projects/${projectId}/contracts/${c.id}`}
                                                data-testid={`ws6-contract-open-${c.id}`}
                                                className="text-[#A8543F] underline">
                                            Open contract
                                        </Link>
                                    </li>))}
                            </ul>)}
                </div>
                <div className="mt-3">
                    <h4 className="font-manrope text-[11px] uppercase text-[#666] tracking-wide">
                        Contract Changes
                    </h4>
                    {changes.length === 0
                        ? <div data-testid="ws6-no-changes" className="text-[#666] text-[12px]">
                            None yet.
                        </div>
                        : (
                            <ul data-testid="ws6-changes-list" className="text-[12px]">
                                {changes.map(ch => (
                                    <li key={ch.id} className="py-0.5"
                                        data-testid={`ws6-change-row-${ch.id}`}>
                                        <span>{ch.description}</span>{" — "}
                                        <span className="text-[#666]"
                                                data-testid={`ws6-change-cost-${ch.id}`}>
                                            cost £{ch.cost_effect || 0}
                                        </span>{" — "}
                                        <Pill tone={ch.status === "effective" ? "good"
                                                    : ch.status === "internal_draft" ? "neutral" : "warn"}
                                                 testid={`ws6-change-status-${ch.id}`}>
                                            {ch.status}
                                        </Pill>
                                        {" · "}
                                        <Link to={`/admin/projects/${projectId}/contract-changes/${ch.id}`}
                                                data-testid={`ws6-change-open-${ch.id}`}
                                                className="text-[#A8543F] underline">
                                            Open change
                                        </Link>
                                    </li>))}
                            </ul>)}
                </div>
            </ModuleCard>

            {/* Module 8 — Package / WS6 Completion (readiness only per §26) */}
            <ModuleCard n={8} title="Package Completion / WS6 Completion (Readiness)"
                            testid="ws6-module-8-completion">
                <div className="text-[12px] text-[#666]"
                        data-testid="ws6-completion-note">
                    Completion workflow is deferred to B12. This surface reflects
                    readiness only, derived from authoritative package records.
                </div>
            </ModuleCard>
        </div>
        </fieldset>);
}

export default WS6ActiveModules;


/* ------------------------------------------------------------------
   Sub-pages per §27 UI routing.
   561-02 Package Detail; 561-03 Review Activation; 561-04 Commercial /
   Contractual Hub; 561-05 Contract Record Detail; 561-06 Contract
   Change Detail.  Each is a thin navigable route reading from
   /api/admin/projects/{pid}/ws6/* — no duplicate stores.
   ------------------------------------------------------------------ */

function Crumbs({ items }) {
    return (
        <nav data-testid="ws6-breadcrumbs"
                className="text-[11px] text-[#666] mb-3">
            {items.map((it, i) => (
                <span key={i}>
                    {it.to ? <Link to={it.to} className="underline">{it.label}</Link>
                          : <span>{it.label}</span>}
                    {i < items.length - 1 ? " › " : null}
                </span>))}
        </nav>);
}


function Page({ testid, title, crumbs, children, error }) {
    const nav = useNavigate();
    return (
        <div className="max-w-[1200px] mx-auto p-6" data-testid={testid}>
            <button className="text-[12px] text-[#666] underline mb-2"
                        onClick={() => nav(-1)}
                        data-testid="ws6-back">
                ← Back
            </button>
            <Crumbs items={crumbs} />
            <h1 className="font-manrope text-[22px] font-semibold mb-4">{title}</h1>
            {error ? <div className="text-red-700 text-[12px]" data-testid="ws6-page-error">{error}</div> : null}
            {children}
        </div>);
}


// -- 561-02 Package Detail ------------------------------------------
export function PackageDetailPage() {
    const { projectId, pkgId } = useParams();
    const [detail, setDetail] = useState(null);
    const [err, setErr] = useState("");
    const pkgLabel = detail?.package?.package_name || "Package";
    usePageMeta({ title: `${pkgLabel} · WS6 Package | Studio G4 Admin` });

    useEffect(() => {
        axios.get(`${API}/admin/projects/${projectId}/ws6/packages/${pkgId}`,
                     { withCredentials: true })
            .then(r => setDetail(r.data))
            .catch(e => setErr(e?.response?.data?.error || e.message));
    }, [projectId, pkgId]);

    if (!detail && !err) return <div className="p-6 text-[12px]">Loading…</div>;
    const p = detail?.package || {};
    const linked = detail?.linked_records || {};
    return (
        <Page testid="ws6-package-detail-page"
                title={`${p.package_name || "Package"} · 561-02 Package Detail`}
                error={err}
                crumbs={[
                    { label: "Projects", to: "/admin/projects" },
                    { label: "Project Workspace", to: `/admin/projects/${projectId}` },
                    { label: p.package_name || "Package" },
                ]}>
            <section className="grid grid-cols-2 gap-4 text-[12px] mb-6">
                <div>
                    <h3 className="font-manrope text-[11px] uppercase text-[#666]">Package Overview</h3>
                    <div>Code: <span data-testid="ws6-pd-code">{p.package_code}</span></div>
                    <div>Status: <span data-testid="ws6-pd-status">{p.status}</span></div>
                    <div>Owner: <span data-testid="ws6-pd-owner">{p.owner || "—"}</span></div>
                    <div>Contract Basis: <span data-testid="ws6-pd-basis">{p.contract_basis || "—"}</span></div>
                    <div>Governing Contract Record:{" "}
                        {p.contract_record_id ? (
                            <Link to={`/admin/projects/${projectId}/contracts/${p.contract_record_id}`}
                                    data-testid="ws6-pd-contract-link"
                                    className="underline text-[#A8543F]">
                                {p.contract_record_id.slice(0, 8)}…
                            </Link>
                        ) : "—"}
                    </div>
                    <div>Start: <span data-testid="ws6-pd-start">{p.activation?.activated_at || "—"}</span></div>
                </div>
                <div>
                    <h3 className="font-manrope text-[11px] uppercase text-[#666]">Scope / Commercial</h3>
                    <div>Pricing: <span data-testid="ws6-pd-pricing">{p.project_configuration?.pricing_method || p.snapshot?.pricing_method || "—"}</span></div>
                    <div>Fee: <span data-testid="ws6-pd-fee">
                        {p.snapshot?.fixed_fee
                            ? `${p.snapshot.fixed_fee.currency || "GBP"} ${p.snapshot.fixed_fee.amount || 0}`
                            : "—"}
                    </span></div>
                    <div>Payment terms: <span data-testid="ws6-pd-payment-terms">
                        {p.project_configuration?.payment_terms
                            ? JSON.stringify(p.project_configuration.payment_terms)
                            : "—"}
                    </span></div>
                    <div className="mt-1">Project-specific scope:</div>
                    <pre className="whitespace-pre-wrap bg-[#F8F6F1] p-2 rounded text-[11px]"
                             data-testid="ws6-pd-scope">
                        {p.project_configuration?.project_specific_scope || "—"}
                    </pre>
                </div>
            </section>
            <section className="text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666] mb-1">
                    Shared Operational Records (linked, not duplicated)
                </h3>
                <ul>
                    <li data-testid="ws6-pd-linked-internal-tasks">
                        Internal Team To-Dos: {linked.internal_tasks?.length || 0}
                    </li>
                    <li data-testid="ws6-pd-linked-client-actions">
                        Client Actions: {linked.client_actions?.length || 0}
                    </li>
                    <li data-testid="ws6-pd-linked-deliverables">
                        Deliverables: {linked.deliverables?.length || 0}
                    </li>
                    <li data-testid="ws6-pd-linked-programme">
                        Programme items: {linked.programme_items?.length || 0}
                    </li>
                    <li data-testid="ws6-pd-linked-files">
                        Files: {linked.files?.length || 0}
                    </li>
                    <li data-testid="ws6-pd-linked-communications">
                        Communications: {linked.communications?.length || 0}
                    </li>
                </ul>
                {p.package_code === "WS6-PCKG-005" ? (
                    <div className="mt-3 text-[11px] text-[#7A4A22]"
                            data-testid="ws6-pd-pm-ca-separation">
                        Project Management scope. Contract Administration duties are NOT included
                        unless a separate CA package/authority exists.
                    </div>
                ) : null}
                {p.status === "Appointed" ? (
                    <div className="mt-4">
                        <Link to={`/admin/projects/${projectId}/packages/${pkgId}/activation`}
                                data-testid="ws6-pd-review-activation"
                                className="inline-block px-3 py-1.5 bg-[#A8543F] text-white rounded text-[12px]">
                            Review Activation
                        </Link>
                    </div>
                ) : null}
            </section>
        </Page>);
}


// -- 561-03 Review Activation ---------------------------------------
export function PackageActivationPage() {
    const { projectId, pkgId } = useParams();
    const [prev, setPrev] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    // Communications Safety Correction — inline confirm for WS6 package
    // activation so admin can opt in / out of the client email.
    const [showConfirm, setShowConfirm] = useState(false);
    const [notifyClient, setNotifyClient] = useState(false);
    usePageMeta({ title: "Review activation · WS6 Package | Studio G4 Admin" });

    const reload = useCallback(() => {
        axios.get(`${API}/admin/projects/${projectId}/ws6/packages/${pkgId}/activation`,
                     { withCredentials: true })
            .then(r => setPrev(r.data))
            .catch(e => setErr(e?.response?.data?.error || e.message));
    }, [projectId, pkgId]);

    useEffect(() => { reload(); }, [reload]);

    const doActivate = async () => {
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws6/packages/${pkgId}/activate`,
                { activation_op_id: `ui-${Date.now()}`,
                     expected_version: prev?.package?.record_version || 0,
                     notify_client: notifyClient },
                { withCredentials: true });
            setShowConfirm(false);
            setNotifyClient(false);
            reload();
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
        finally { setBusy(false); }
    };

    if (!prev && !err) return <div className="p-6 text-[12px]">Loading…</div>;
    const p = prev?.package || {};
    const ca = prev?.contractual_authority || {};
    const preq = prev?.prerequisites || [];
    const ac = prev?.activation_conditions || [];
    const inst = prev?.preview_records_to_instantiate || {};
    return (
        <Page testid="ws6-activation-page"
                title={`Review Activation · ${p.package_name || "Package"}`}
                error={err}
                crumbs={[
                    { label: "Projects", to: "/admin/projects" },
                    { label: "Project Workspace", to: `/admin/projects/${projectId}` },
                    { label: p.package_name || "Package",
                          to: `/admin/projects/${projectId}/packages/${pkgId}` },
                    { label: "Review Activation" },
                ]}>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">A. Activation Summary</h3>
                <div>Status: <span data-testid="ws6-ap-status">{p.status}</span></div>
                <div>Ready: <span data-testid="ws6-ap-ready">{prev.ready ? "yes" : "no"}</span></div>
                <div>Blockers: <span data-testid="ws6-ap-blockers-count">{prev.blockers?.length || 0}</span></div>
                {prev.blockers?.length ? (
                    <ul data-testid="ws6-ap-blockers-list" className="list-disc pl-5 text-[11px] text-[#7A4A22]">
                        {prev.blockers.map((b, i) => (
                            <li key={i} data-testid={`ws6-ap-blocker-${i}`}>
                                <strong>{b.title || b}</strong>
                                {b.reason ? <> — <span className="text-[#666]">{b.reason}</span></> : null}
                            </li>))}
                    </ul>
                ) : null}
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">B. Contractual Authority</h3>
                <div>Basis: <span data-testid="ws6-ap-basis">{ca.contract_basis || "—"}</span></div>
                <div>Executed: <span data-testid="ws6-ap-executed">{ca.executed ? "yes" : "no"}</span></div>
                <div>Effective: <span data-testid="ws6-ap-effective">{ca.effective ? "yes" : "no"}</span></div>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">
                    C. Prerequisites ({preq.length})
                </h3>
                <ul data-testid="ws6-ap-prereqs" className="text-[11px]">
                    {preq.map((r, i) => (
                        <li key={i} data-testid={`ws6-ap-prereq-${i}`}
                                 className="border-t border-[#F0EDE7] py-1">
                            <strong>{r.name}</strong> · classification: {r.classification || "—"} ·
                            status: {r.satisfied ? "satisfied" : "unsatisfied"} ·
                            {r.waivable ? " waivable" : " NON-WAIVABLE"}
                            {r.waived ? ` · waived (${r.waiver?.reason || ""})` : null}
                        </li>))}
                </ul>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">
                    F. Activation Conditions ({ac.length})
                </h3>
                <ul data-testid="ws6-ap-activation-conditions" className="text-[11px]">
                    {ac.map((r, i) => (
                        <li key={i} data-testid={`ws6-ap-ac-${i}`}>
                            {r.name} — {r.satisfied ? "satisfied" : "unsatisfied"}
                        </li>))}
                </ul>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">G. Preview — Records to Instantiate</h3>
                <ul className="text-[11px]" data-testid="ws6-ap-preview">
                    <li>Internal Team To-Dos: {inst.internal_tasks?.length || 0}</li>
                    <li>Client Actions: {inst.client_actions?.length || 0}</li>
                    <li>Deliverables: {inst.deliverables?.length || 0}</li>
                    <li>Programme items: {inst.programme_items?.length || 0}</li>
                    <li>Mini Programme: {inst.mini_programme_items?.length || 0}</li>
                </ul>
                <div className="mt-1 text-[10px] text-[#666]">
                    Previews — not yet instantiated. Instantiation occurs on Activate only.
                </div>
            </section>
            <button data-testid="ws6-ap-activate-btn"
                    disabled={!prev.ready || busy || p.status !== "Appointed"}
                    onClick={() => { setNotifyClient(false); setShowConfirm(true); }}
                    className="px-3 py-1.5 rounded bg-[#A8543F] text-white text-[12px] disabled:opacity-40">
                Activate Package
            </button>
            {showConfirm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
                    data-testid="ws6-activate-confirm">
                    <div className="bg-white rounded shadow-lg w-[min(480px,92vw)] p-4">
                        <h3 className="font-manrope text-[14px] font-semibold text-charcoal">
                            Activate package
                        </h3>
                        <p className="text-[12px] text-[#666] mt-1">
                            Activate <strong>{p.package_name}</strong>? This instantiates
                            operational content and cannot be undone from this page.
                        </p>
                        <label className="text-[12px] flex items-center gap-2 mt-3">
                            <input type="checkbox" checked={notifyClient}
                                onChange={(e) => setNotifyClient(e.target.checked)}
                                data-testid="ws6-activate-notify-client" />
                            Send client notification
                        </label>
                        <div className="mt-4 flex justify-end gap-2">
                            <button type="button"
                                onClick={() => { setShowConfirm(false); setNotifyClient(false); }}
                                className="text-[12px] px-3 py-1.5"
                                data-testid="ws6-activate-cancel">Cancel</button>
                            <button type="button" onClick={doActivate}
                                disabled={busy}
                                className="text-[12px] px-3 py-1.5 rounded bg-[#A8543F] text-white"
                                data-testid="ws6-activate-confirm-btn">
                                {busy ? "Activating…" : "Activate"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Page>);
}


// -- 561-04 Commercial / Contractual Hub ----------------------------
export function CommercialHubPage() {
    const { projectId } = useParams();
    const [hub, setHub] = useState(null);
    const [err, setErr] = useState("");
    usePageMeta({ title: "Commercial hub · Project | Studio G4 Admin" });
    useEffect(() => {
        axios.get(`${API}/admin/projects/${projectId}/ws6/commercial-hub`,
                     { withCredentials: true })
            .then(r => setHub(r.data))
            .catch(e => setErr(e?.response?.data?.error || e.message));
    }, [projectId]);
    if (!hub && !err) return <div className="p-6 text-[12px]">Loading…</div>;
    return (
        <Page testid="ws6-commercial-hub-page"
                title="Commercial / Contractual Hub"
                error={err}
                crumbs={[
                    { label: "Projects", to: "/admin/projects" },
                    { label: "Project Workspace", to: `/admin/projects/${projectId}` },
                    { label: "Commercial / Contractual Hub" },
                ]}>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">A. Current Appointed Scope</h3>
                <ul data-testid="ws6-hub-appointed-scope" className="text-[11px]">
                    {(hub.current_appointed_scope || []).map((sc, i) => (
                        <li key={i} data-testid={`ws6-hub-scope-item-${i}`}>
                            {sc.package_name} — {sc.status} — {sc.contract_basis}
                        </li>))}
                </ul>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">B. Governing Contract Records</h3>
                <ul data-testid="ws6-hub-contracts" className="text-[11px]">
                    {(hub.governing_contract_records || []).map(c => (
                        <li key={c.id} data-testid={`ws6-hub-contract-${c.id}`}>
                            <Link to={`/admin/projects/${projectId}/contracts/${c.id}`}
                                    className="underline text-[#A8543F]">
                                {c.contract_title}
                            </Link>{" — "}
                            {c.contract_basis}{" · "}
                            executed: {c.execution_state}{" · "}
                            effective: {c.effective_state}
                        </li>))}
                </ul>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">F. Payment / Certification Summary</h3>
                <div data-testid="ws6-hub-paid-summary">
                    Paid to date: {hub.commercial_summary?.paid_to_date || "Not Confirmed in Platform"}
                </div>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">D. Appointment Addenda</h3>
                <div data-testid="ws6-hub-lrs-addenda" className="text-[11px] text-[#666]">
                    Addenda are managed via the Appointment / Addendum flows (B8). No external-contract
                    changes appear here.
                </div>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">E. External / Construction Contract Changes</h3>
                <ul data-testid="ws6-hub-external-changes" className="text-[11px]">
                    {(hub.changes || []).map(ch => (
                        <li key={ch.id} data-testid={`ws6-hub-change-${ch.id}`}>
                            <Link to={`/admin/projects/${projectId}/contract-changes/${ch.id}`}
                                    className="underline text-[#A8543F]">
                                {ch.reference || ch.id.slice(0, 8)}
                            </Link>{" — "}
                            {ch.description}{" — cost £"}{ch.cost_effect || 0}{" — "}{ch.status}
                        </li>))}
                </ul>
            </section>
        </Page>);
}


// -- 561-05 Contract Record Detail ----------------------------------
const _EXTERNAL_BASES = new Set([
    "external_standard_form_contract",
    "bespoke_external_contract",
    "other_approved_instrument",
]);

function _emptySigner() {
    return {
        signer_name: "",
        signer_email: "",
        signer_role: "",
        signed_on: new Date().toISOString().slice(0, 10),
        signature_method: "zoho_sign",
        external_envelope_ref: "",
        notes: "",
    };
}

export function ContractDetailPage() {
    const { projectId, contractId } = useParams();
    const [detail, setDetail] = useState(null);
    const [err, setErr] = useState("");
    const [showRecordExec, setShowRecordExec] = useState(false);
    const [toast, setToast] = useState("");
    const [effBusy, setEffBusy] = useState(false);
    const contractLabel = detail?.contract?.contract_title || "Contract";
    usePageMeta({ title: `${contractLabel} · Contract | Studio G4 Admin` });

    const loadDetail = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws6/contracts/${contractId}`,
                { withCredentials: true });
            setDetail(r.data);
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
    }, [projectId, contractId]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    const markEffective = async () => {
        setEffBusy(true);
        setErr("");
        try {
            const c = detail?.contract || {};
            await axios.post(
                `${API}/admin/projects/${projectId}/ws5/contracts/${contractId}/mark-effective`,
                { expected_version: c.record_version,
                  operation_id: _uuid4() },
                { withCredentials: true });
            setToast("Contract marked effective.");
            await loadDetail();
        } catch (e) {
            setErr(e?.response?.data?.reason
                   || e?.response?.data?.error || e.message);
        } finally {
            setEffBusy(false);
        }
    };

    if (!detail && !err) return <div className="p-6 text-[12px]">Loading…</div>;
    const c = detail?.contract || {};
    const parts = c.particulars || {};
    const linked = detail?.linked_packages || [];
    const chgs = detail?.changes || [];
    const acts = detail?.formal_contractual_actions || [];
    const executedDocs = c.executed_documents || [];
    const ev = c.execution_evidence || {};
    const extSigners = detail?.external_signers || [];
    const showRecordExecBtn =
        _EXTERNAL_BASES.has(c.contract_basis)
        && c.execution_state === "not_executed";
    const showMarkEffectiveBtn =
        c.execution_state === "executed"
        && c.effective_state === "not_effective";

    return (
        <Page testid="ws6-contract-detail-page"
                title={c.contract_title || "Contract Record"}
                error={err}
                crumbs={[
                    { label: "Projects", to: "/admin/projects" },
                    { label: "Project Workspace", to: `/admin/projects/${projectId}` },
                    { label: "Commercial / Contractual Hub",
                          to: `/admin/projects/${projectId}/commercial-hub` },
                    { label: c.contract_title || "Contract" },
                ]}>
            {toast && (
                <div data-testid="ws6-cd-toast"
                     className="mb-3 text-[12px] text-green-800 bg-green-50 border border-green-200 rounded p-2">
                    {toast}
                </div>
            )}
            <section className="mb-4 text-[12px] grid grid-cols-2 gap-4">
                <div>
                    <h3 className="font-manrope text-[11px] uppercase text-[#666]">A. Contract Identity</h3>
                    <div>Reference: <span data-testid="ws6-cd-ref">{c.id?.slice(0, 12)}</span></div>
                    <div>Basis: <span data-testid="ws6-cd-basis">{c.contract_basis}</span></div>
                    <div>Form: <span data-testid="ws6-cd-form">{c.contract_form || "—"}</span></div>
                    <div>Status: <span data-testid="ws6-cd-status">{c.status || "—"}</span></div>
                </div>
                <div>
                    <h3 className="font-manrope text-[11px] uppercase text-[#666]">B. Execution / Effectiveness (distinct)</h3>
                    <div>Executed: <span data-testid="ws6-cd-executed">{c.execution_state}</span></div>
                    <div>Executed date: <span data-testid="ws6-cd-executed-at">{c.executed_at || "—"}</span></div>
                    <div>Effective: <span data-testid="ws6-cd-effective">{c.effective_state}</span></div>
                    <div>Effective date: <span data-testid="ws6-cd-effective-at">{c.effective_at || "—"}</span></div>
                </div>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">D. Contract Particulars (form-driven)</h3>
                <pre className="whitespace-pre-wrap bg-[#F8F6F1] p-2 rounded text-[11px]"
                         data-testid="ws6-cd-particulars">
                    {JSON.stringify(parts, null, 2)}
                </pre>
                <div className="text-[10px] text-[#666]">
                    Contractual dates shown here are contractual — not Programme dates. Programme edits do not
                    silently rewrite them.
                </div>
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">E. Linked Service Packages</h3>
                <ul data-testid="ws6-cd-linked-packages" className="text-[11px]">
                    {linked.map(p => (
                        <li key={p.id} data-testid={`ws6-cd-linked-pkg-${p.id}`}>
                            <Link to={`/admin/projects/${projectId}/packages/${p.id}`}
                                    className="underline text-[#A8543F]">
                                {p.package_name}
                            </Link> — {p.status}
                        </li>))}
                </ul>
            </section>
            <section className="mb-4 text-[12px]">
                <div className="flex items-baseline justify-between">
                    <h3 className="font-manrope text-[11px] uppercase text-[#666]">F. Executed Contract Document (immutable)</h3>
                    <div className="flex items-center gap-2">
                        {showRecordExecBtn && (
                            <>
                                <button type="button"
                                        disabled
                                        aria-disabled="true"
                                        title="Coming soon — in-app creation with e-signature. Use 'Upload Signed Contract' for externally-signed documents."
                                        data-testid="ws6-cd-create-disabled-btn"
                                        className="text-[11px] px-3 py-1 rounded border border-[#E5E2DC] text-[#999] bg-[#F5F3EE] cursor-not-allowed">
                                    Create Contract
                                </button>
                                <button type="button"
                                        onClick={() => setShowRecordExec(true)}
                                        data-testid="ws6-cd-record-ext-exec-btn"
                                        className="text-[11px] px-3 py-1 rounded bg-[#A8543F] text-white">
                                    Upload Signed Contract
                                </button>
                            </>
                        )}
                        {showMarkEffectiveBtn && (
                            <button type="button"
                                    onClick={markEffective}
                                    disabled={effBusy}
                                    data-testid="ws6-cd-mark-effective-btn"
                                    className="text-[11px] px-3 py-1 rounded bg-[#345E34] text-white">
                                {effBusy ? "Marking…" : "Mark Effective"}
                            </button>
                        )}
                    </div>
                </div>
                {executedDocs.length > 0 ? (
                    <div data-testid="ws6-cd-executed-docs" className="mt-2 flex flex-col gap-1">
                        {executedDocs.map((ed, idx) => (
                            <div key={ed.file_id || idx}
                                 data-testid={`ws6-cd-executed-doc-${idx}`}>
                                <a href={`${API}/admin/files/${ed.file_id}/download`}
                                   data-testid={`ws6-cd-executed-doc-link-${idx}`}
                                   className="underline text-[#A8543F]">
                                    Download executed PDF
                                    {executedDocs.length > 1 ? ` (${idx + 1}/${executedDocs.length})` : ""}
                                </a>
                                <span className="ml-2 font-mono text-[11px] text-[#444]">
                                    {ed.filename}
                                </span>
                                {" · "}
                                <span className="text-[10px] text-[#666]">
                                    v{ed.current_version_number} · immutable via A4 Files
                                </span>
                            </div>
                        ))}
                        {ev.method_summary && (
                            <div data-testid="ws6-cd-method-summary">
                                <strong>Method:</strong> {ev.method_summary}
                            </div>
                        )}
                        {ev.external_ref && (
                            <div data-testid="ws6-cd-external-ref">
                                <strong>External ref:</strong> {ev.external_ref}
                            </div>
                        )}
                        {extSigners.length > 0 && (
                            <div data-testid="ws6-cd-signers" className="mt-2">
                                <strong>Signers:</strong>
                                <ul className="list-disc ml-5 text-[11px]">
                                    {extSigners.map(s => (
                                        <li key={s.id} data-testid={`ws6-cd-signer-${s.id}`}>
                                            {s.signer_name} — {s.signer_role}
                                            {" · signed "}{s.signed_on}
                                            {" · "}
                                            <span className="inline-flex rounded-full bg-[#F5F3EE] px-2 py-0.5 text-[10px]">
                                                {_METHOD_LABELS[s.signature_method] || s.signature_method}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ) : (
                    <div data-testid="ws6-cd-no-executed-doc" className="text-[#666] mt-2">
                        Not yet executed.
                    </div>
                )}
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">G. Changes / Addenda</h3>
                {c.contract_basis === "lrs_paid_services_appointment" ? (
                    <div data-testid="ws6-cd-lrs-addenda-note" className="text-[11px] text-[#666]">
                        Appointment basis — changes route via the Appointment / Addendum flow (B8),
                        not the external Contract Change register.
                    </div>
                ) : (
                    <ul data-testid="ws6-cd-changes" className="text-[11px]">
                        {chgs.map(ch => (
                            <li key={ch.id} data-testid={`ws6-cd-change-${ch.id}`}>
                                <Link to={`/admin/projects/${projectId}/contract-changes/${ch.id}`}
                                        className="underline text-[#A8543F]">
                                    {ch.reference || ch.id.slice(0, 8)}
                                </Link>{" — "}
                                {ch.description} — {ch.status}
                            </li>))}
                    </ul>
                )}
            </section>
            <section className="mb-4 text-[12px]">
                <h3 className="font-manrope text-[11px] uppercase text-[#666]">
                    I. Formal Contractual Actions (distinct from Communications / Client Actions)
                </h3>
                <ul data-testid="ws6-cd-formal-actions" className="text-[11px]">
                    {acts.length === 0
                        ? <li className="text-[#666]">None recorded.</li>
                        : acts.map((a, i) => (
                            <li key={i} data-testid={`ws6-cd-formal-action-${i}`}>
                                {a.action_type} — {a.issued_at || "pending"} — clause {a.clause_reference || "—"}
                            </li>))}
                </ul>
            </section>
            <RecordExternalExecutionModal
                open={showRecordExec}
                onClose={() => setShowRecordExec(false)}
                endpointUrl={`${API}/admin/projects/${projectId}/ws5/contracts/${contractId}/record-external-execution`}
                submitLabel="Record Execution"
                helpText={"Attach an externally-signed contract PDF and capture structured signer evidence. "
                         + "The contract will be marked executed but not effective — click \"Mark Effective\" "
                         + "separately once it takes effect."}
                testIdPrefix="ws6-rec-ext-exec"
                projectId={projectId}
                onSuccessData={async () => {
                    setToast("External execution recorded. Mark effective when ready.");
                    await loadDetail();
                }} />
        </Page>);
}


// -- 561-06 Contract Change Detail ----------------------------------
export function ContractChangeDetailPage() {
    const { projectId, changeId } = useParams();
    const [change, setChange] = useState(null);
    const [parentContract, setParentContract] = useState(null);
    const [extSigners, setExtSigners] = useState([]);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [showExecInst, setShowExecInst] = useState(false);
    const [toast, setToast] = useState("");
    const changeLabel = change?.reference || "Contract change";
    usePageMeta({ title: `${changeLabel} · Change | Studio G4 Admin` });
    const reload = useCallback(() => {
        axios.get(`${API}/admin/projects/${projectId}/ws6/contract-changes/${changeId}`,
                     { withCredentials: true })
            .then(r => {
                setChange(r.data?.change || null);
                setParentContract(r.data?.parent_contract || null);
                setExtSigners(r.data?.external_signers || []);
                if (!r.data?.change) setErr("contract_change_not_found");
            })
            .catch(e => setErr(e?.response?.data?.error || e.message));
    }, [projectId, changeId]);
    useEffect(() => { reload(); }, [reload]);
    if (!change && !err) return <div className="p-6 text-[12px]">Loading…</div>;

    const call = async (path, body) => {
        setBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws6/contract-changes/${changeId}${path}`,
                body, { withCredentials: true });
            reload();
        } catch (e) { setErr(e?.response?.data?.error || e.message); }
        finally { setBusy(false); }
    };

    const mech = parentContract?.change_effectiveness_mechanism;
    const execDocs = change?.executed_documents || [];
    const execEv = change?.execution_evidence || {};
    const showExecInstBtn = mech === "executed_change_instrument_required"
        && change && !["effective", "rejected", "withdrawn",
                       "superseded", "cancelled"].includes(change.status);

    return (
        <Page testid="ws6-change-detail-page"
                title={`Contract Change · ${change?.reference || change?.id?.slice(0, 8)}`}
                error={err}
                crumbs={[
                    { label: "Projects", to: "/admin/projects" },
                    { label: "Project Workspace", to: `/admin/projects/${projectId}` },
                    { label: "Commercial / Contractual Hub",
                          to: `/admin/projects/${projectId}/commercial-hub` },
                    { label: "Contract Change" },
                ]}>
            {change ? (
                <>
                    <section className="mb-4 text-[12px] grid grid-cols-2 gap-4">
                        <div>
                            <div>Reference: <span data-testid="ws6-cchg-ref">{change.reference}</span></div>
                            <div>Type: <span data-testid="ws6-cchg-type">{change.change_type}</span></div>
                            <div>Status: <span data-testid="ws6-cchg-status">{change.status}</span></div>
                            <div>Client visibility: <span data-testid="ws6-cchg-visibility">{change.client_visibility}</span></div>
                            <div>Contract:{" "}
                                <Link data-testid="ws6-cchg-contract-link"
                                        className="underline text-[#A8543F]"
                                        to={`/admin/projects/${projectId}/contracts/${change.contract_record_id}`}>
                                    {change.contract_record_id?.slice(0, 12)}
                                </Link>
                            </div>
                        </div>
                        <div>
                            <div>Client approval:{" "}
                                <span data-testid="ws6-cchg-client-approval">
                                    {change.client_approval_state}
                                </span>
                            </div>
                            <div>Instruction state:{" "}
                                <span data-testid="ws6-cchg-instruction-state">
                                    {change.instruction_state}
                                </span>
                            </div>
                            <div>Cost effect: <span data-testid="ws6-cchg-cost">£{change.cost_effect || 0}</span>
                                {" "}
                                <span className="text-[10px] text-[#666]"
                                        data-testid="ws6-cchg-cost-note">
                                    {change.status === "effective"
                                        ? "feeds Adjusted Contract Sum"
                                        : "does NOT feed Adjusted Contract Sum until effective"}
                                </span>
                            </div>
                            <div>Time effect: <span data-testid="ws6-cchg-time">{change.time_effect_days || 0} days</span>
                                {" "}
                                <span className="text-[10px] text-[#666]"
                                        data-testid="ws6-cchg-time-note">
                                    {change.status === "effective"
                                        ? "may update contractual completion date via formal mechanism"
                                        : "does NOT rewrite contractual completion date until effective"}
                                </span>
                            </div>
                        </div>
                    </section>
                    <section className="mb-4 text-[12px]">
                        <h3 className="font-manrope text-[11px] uppercase text-[#666]">Description</h3>
                        <div data-testid="ws6-cchg-description">{change.description}</div>
                    </section>
                    <section className="mb-4 text-[12px]">
                        <h3 className="font-manrope text-[11px] uppercase text-[#666]">Audit / History (append-only)</h3>
                        <ul data-testid="ws6-cchg-history" className="text-[11px]">
                            {(change.history || []).map((h, i) => (
                                <li key={i} data-testid={`ws6-cchg-history-${i}`}>
                                    {h.at} — {h.event} — {h.by || "—"}
                                </li>))}
                        </ul>
                    </section>
                    <section className="flex gap-2 text-[12px]">
                        {change.status === "internal_draft" ? (
                            <button data-testid="ws6-cchg-expose-btn"
                                    disabled={busy}
                                    onClick={() => call("/expose-client",
                                        { op_id: `ui-expose-${Date.now()}`,
                                            expected_version: change.record_version })}
                                    className="px-3 py-1.5 rounded bg-[#F5F3EE] text-[#1D1D1B]">
                                Expose to Client
                            </button>
                        ) : null}
                        {change.status === "client_visible_pending" ? (
                            <>
                                <button data-testid="ws6-cchg-approve-btn"
                                        disabled={busy}
                                        onClick={() => call("/client-approve",
                                            { op_id: `ui-approve-${Date.now()}`,
                                                expected_version: change.record_version })}
                                        className="px-3 py-1.5 rounded bg-[#E4EFE4] text-[#345E34]">
                                    Record Client Approval
                                </button>
                                <button data-testid="ws6-cchg-reject-btn"
                                        disabled={busy}
                                        onClick={() => call("/reject",
                                            { op_id: `ui-reject-${Date.now()}`,
                                                expected_version: change.record_version })}
                                        className="px-3 py-1.5 rounded bg-[#FBEEDF] text-[#7A4A22]">
                                    Reject
                                </button>
                            </>
                        ) : null}
                        {change.status === "client_approved" ? (
                            <button data-testid="ws6-cchg-instruct-btn"
                                    disabled={busy}
                                    onClick={() => call("/formally-instruct",
                                        { op_id: `ui-instruct-${Date.now()}`,
                                            expected_version: change.record_version,
                                            action_type: "variation_instruction",
                                            clause_reference: "TBC" })}
                                    className="px-3 py-1.5 rounded bg-[#A8543F] text-white">
                                Formally Instruct (make effective)
                            </button>
                        ) : null}
                        {showExecInstBtn ? (
                            <>
                                <button type="button"
                                        disabled
                                        aria-disabled="true"
                                        title="Coming soon — in-app creation with e-signature. Use 'Upload Signed Change Instrument' for externally-signed documents."
                                        data-testid="ws6-cchg-create-disabled-btn"
                                        className="px-3 py-1.5 rounded border border-[#E5E2DC] text-[#999] bg-[#F5F3EE] cursor-not-allowed">
                                    Create Change Instrument
                                </button>
                                <button data-testid="ws6-cchg-record-exec-inst-btn"
                                        disabled={busy}
                                        onClick={() => setShowExecInst(true)}
                                        className="px-3 py-1.5 rounded bg-[#A8543F] text-white">
                                    Upload Signed Change Instrument
                                </button>
                            </>
                        ) : null}
                    </section>
                    {toast && (
                        <div data-testid="ws6-cchg-toast"
                             className="mt-3 text-[12px] text-green-800 bg-green-50 border border-green-200 rounded p-2">
                            {toast}
                        </div>
                    )}
                    {execDocs.length > 0 && (execEv.method_summary || extSigners.length > 0) && (
                        <section className="mt-4 text-[12px] bg-[#FCFBF9] border border-[#E5E2DC] rounded p-3"
                                 data-testid="ws6-cchg-exec-inst-panel">
                            <h3 className="font-manrope text-[11px] uppercase text-[#666] mb-1">
                                Executed Change Instrument{execDocs.length > 1 ? "s" : ""} (immutable)
                            </h3>
                            {execDocs.map((ed, idx) => (
                                <div key={ed.file_id || idx}
                                     data-testid={`ws6-cchg-exec-inst-${idx}`}>
                                    <a href={`${API}/admin/files/${ed.file_id}/download`}
                                       data-testid={`ws6-cchg-exec-inst-link-${idx}`}
                                       className="underline text-[#A8543F]">
                                        Download executed PDF
                                        {execDocs.length > 1 ? ` (${idx + 1}/${execDocs.length})` : ""}
                                    </a>
                                    <span className="ml-2 font-mono text-[11px] text-[#444]">
                                        {ed.filename}
                                    </span>
                                    {" · "}
                                    <span className="text-[10px] text-[#666]">
                                        v{ed.current_version_number} · immutable via A4 Files
                                    </span>
                                </div>
                            ))}
                            {execEv.method_summary && (
                                <div data-testid="ws6-cchg-exec-inst-method">
                                    <strong>Method:</strong> {execEv.method_summary}
                                </div>
                            )}
                            {execEv.external_ref && (
                                <div data-testid="ws6-cchg-exec-inst-ref">
                                    <strong>External ref:</strong> {execEv.external_ref}
                                </div>
                            )}
                            {extSigners.length > 0 && (
                                <div data-testid="ws6-cchg-exec-inst-signers" className="mt-1">
                                    <strong>Signers:</strong>
                                    <ul className="list-disc ml-5 text-[11px]">
                                        {extSigners.map(s => (
                                            <li key={s.id} data-testid={`ws6-cchg-exec-inst-signer-${s.id}`}>
                                                {s.signer_name} — {s.signer_role}
                                                {" · signed "}{s.signed_on}
                                                {" · "}
                                                <span className="inline-flex rounded-full bg-[#F5F3EE] px-2 py-0.5 text-[10px]">
                                                    {_METHOD_LABELS[s.signature_method] || s.signature_method}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>
                    )}
                    <RecordExternalExecutionModal
                        open={showExecInst}
                        onClose={() => setShowExecInst(false)}
                        endpointUrl={`${API}/admin/projects/${projectId}/ws6/contract-changes/${changeId}/record-executed-instrument`}
                        submitLabel="Record Instrument"
                        helpText={"Attach the externally-signed change instrument PDF and capture structured "
                                 + "signer evidence.  The change will be marked effective in a single step."}
                        testIdPrefix="ws6-cchg-rec-exec-inst"
                        projectId={projectId}
                        onSuccessData={() => {
                            setToast("Change instrument recorded and change marked effective.");
                            reload();
                        }} />
                </>
            ) : null}
        </Page>);
}
